import { useState, useEffect } from 'react';
import { getAdminSettings, updateAdminSettings } from '../../utils/api';
import toast from 'react-hot-toast';
import { FiSave, FiTruck, FiInfo } from 'react-icons/fi';
import styles from './AdminTable.module.css';

export default function AdminSettings() {
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [shipping, setShipping] = useState({
    inside_dhaka:            60,
    outside_dhaka:           120,
    free_shipping_threshold: 999,
    free_shipping_enabled:   true,
  });

  useEffect(() => {
    getAdminSettings('shipping')
      .then(r => {
        if (r.data.shipping) setShipping(r.data.shipping);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAdminSettings({ shipping });
      toast.success('✅ Shipping settings saved!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const s = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : Number(e.target.value);
    setShipping(p => ({ ...p, [field]: val }));
  };

  if (loading) return <div className="spinner-center"><div className="spinner" /></div>;

  // Live preview calculation
  const previewDhakaFree    = shipping.free_shipping_enabled && 1000 >= shipping.free_shipping_threshold ? 0 : shipping.inside_dhaka;
  const previewDhakaNotFree = shipping.free_shipping_enabled && 500 >= shipping.free_shipping_threshold  ? 0 : shipping.inside_dhaka;

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>Settings</h2>
          <p className={styles.pageSubtitle}>Configure shipping rates and store settings</p>
        </div>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          <FiSave size={15} /> {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      {/* Shipping card */}
      <div className="card" style={{ padding: '1.5rem', maxWidth: 640 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1.25rem', paddingBottom: '1rem', borderBottom: '1px solid var(--border)' }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
            <FiTruck size={20} />
          </div>
          <div>
            <h3 style={{ fontWeight: 700, fontSize: '1rem' }}>Shipping Rates</h3>
            <p style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>Changes apply immediately to all new orders</p>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>

          {/* Inside Dhaka */}
          <div className="form-group">
            <label className="form-label">
              Inside Dhaka — Delivery Charge (৳)
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-muted)', flexShrink: 0 }}>৳</span>
              <input
                type="number" className="form-input" min={0}
                value={shipping.inside_dhaka}
                onChange={s('inside_dhaka')}
                style={{ maxWidth: 160 }}
              />
            </div>
          </div>

          {/* Outside Dhaka */}
          <div className="form-group">
            <label className="form-label">
              Outside Dhaka — Delivery Charge (৳)
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-muted)', flexShrink: 0 }}>৳</span>
              <input
                type="number" className="form-input" min={0}
                value={shipping.outside_dhaka}
                onChange={s('outside_dhaka')}
                style={{ maxWidth: 160 }}
              />
            </div>
          </div>

          {/* Free shipping toggle */}
          <div style={{ background: 'var(--bg)', borderRadius: 10, padding: '1rem', border: '1px solid var(--border)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: '.625rem' }}>
              <input
                type="checkbox"
                checked={shipping.free_shipping_enabled}
                onChange={s('free_shipping_enabled')}
                style={{ width: 18, height: 18, accentColor: 'var(--primary)', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 700, fontSize: '.9rem' }}>Enable Free Shipping Threshold</span>
            </label>
            <p style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginBottom: shipping.free_shipping_enabled ? '.875rem' : 0 }}>
              When enabled, orders above the threshold get free delivery inside Dhaka.
            </p>

            {shipping.free_shipping_enabled && (
              <div className="form-group">
                <label className="form-label">Free Shipping Minimum Order Amount (৳)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-muted)', flexShrink: 0 }}>৳</span>
                  <input
                    type="number" className="form-input" min={0}
                    value={shipping.free_shipping_threshold}
                    onChange={s('free_shipping_threshold')}
                    style={{ maxWidth: 160 }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Live Preview */}
          <div style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 10, padding: '1rem' }}>
            <p style={{ fontWeight: 700, fontSize: '.82rem', color: '#166534', marginBottom: '.75rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiInfo size={14} /> Live Preview — How customers will see shipping
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { label: 'Dhaka — Order ৳1,000',  fee: previewDhakaFree },
                { label: 'Dhaka — Order ৳500',    fee: previewDhakaNotFree },
                { label: 'Chittagong — Any order', fee: shipping.outside_dhaka },
                { label: 'Rajshahi — Any order',   fee: shipping.outside_dhaka },
              ].map(row => (
                <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '.82rem' }}>
                  <span style={{ color: '#374151' }}>{row.label}</span>
                  <span style={{ fontWeight: 800, color: row.fee === 0 ? '#16a34a' : '#dc2626' }}>
                    {row.fee === 0 ? '🎉 FREE' : `৳${row.fee}`}
                  </span>
                </div>
              ))}
            </div>
          </div>

        </div>

        <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
          <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>
            <FiSave size={16} /> {saving ? 'Saving…' : 'Save Shipping Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}
